"use client";

import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Credenza, CredenzaContent, CredenzaHeader, CredenzaBody, CredenzaTitle, CredenzaDescription } from "@/components/shared/credenza";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { CustomForm } from "@/components/forms";

interface FormModalProps {
    open?: boolean;
    onOpenChange: (open: boolean) => void;
    formComponent: ReactNode;
    title?: string;
    description?: string;
    className?: string;
}

export function FormModal({
    open,
    onOpenChange,
    formComponent,
    title = "Contact Form",
    description = "Get in touch with us",
    className = "",
}: FormModalProps) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) {
        return null;
    }

    return (
        <Credenza open={open} onOpenChange={onOpenChange}>
            <CredenzaContent className="mx-auto w-full sm:w-5/12 max-h-[90vh] overflow-y-auto">
                <CredenzaHeader>
                    <VisuallyHidden>
                        <CredenzaTitle>
                            {title}
                        </CredenzaTitle>
                        <CredenzaDescription>
                            {description}
                        </CredenzaDescription>
                    </VisuallyHidden>
                </CredenzaHeader>
                <CredenzaBody className="mt-5">
                    <div className={`flex w-full justify-center ${className}`}>
                        {formComponent}
                    </div>
                </CredenzaBody>
            </CredenzaContent>
        </Credenza>
    );
}

// Pre-configured modal components for specific use cases

export const LTIFormModal = (props: Omit<FormModalProps, 'formComponent'>) => (
    <FormModal
        {...props}
        formComponent={<CustomForm formId="d1003-download-integrations-guide" />}
    />
);

export const WaitlistFormModal = (props: Omit<FormModalProps, 'formComponent' | 'title' | 'description'>) => (
    <FormModal
        {...props}
        formComponent={<CustomForm formId="w1000-waitlist-students" />}
        title="Join our waitlist"
        description="We'll be in touch soon!"
    />
);

export const EABCaseStudyFormModal = (props: Omit<FormModalProps, 'formComponent' | 'title' | 'description'>) => (
    <FormModal
        {...props}
        formComponent={<CustomForm formId="e1002-eab-evaluation-paper-usa" />}
        title="Download EAB Case Study"
        description="Fill out the form to download our EAB case study"
    />
);

export const UoBEvaluationFormModal = (props: Omit<FormModalProps, 'formComponent' | 'title' | 'description'>) => (
    <FormModal
        {...props}
        formComponent={<CustomForm formId="e1000-uob-evaluation-paper" />}
        title="Download UoB Evaluation"
        description="Fill out the form to download our University of Birmingham evaluation"
    />
);

export const PerspectivePieceFormModal = (props: Omit<FormModalProps, 'formComponent' | 'title' | 'description'>) => (
    <FormModal
        {...props}
        formComponent={<CustomForm formId="d1000-download-ai-in-higher-education" />}
        title="Download Generative AI in Higher Education Report"
        description="Fill out the form to download our perspective piece on Generative AI in Higher Education"
    />
);

export const BusinessInfluencerFormModal = (props: Omit<FormModalProps, 'formComponent' | 'title' | 'description'>) => (
    <FormModal
        {...props}
        formComponent={<CustomForm formId="d1001-download-the-business-influencer" />}
        title="Download The Business Influencer Article"
        description="Fill out the form to download our article: Agility, Ambition and Audacity: Lessons from taking StudyStash Stateside"
    />
);
